import { isAfter } from "date-fns";

import { config } from "config/config";
import { ApiManager } from "services/api";
import { IAgent } from "store/entities/agents";

import { IAnalyticsIdentity } from "./analytics";

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

window.dataLayer = window.dataLayer || [];

// Cutoff for the new way of sending the signup event.
// Only licenses created after this date will have the new event sent.
const SIGNUP_EVENT_CUTOFF = "2024-11-14T11:00:00.000Z"; // 12AM GMT+1

export function installGTM(gtmId: string): void {
  const gtmScriptId = "gtm-script";
  const gtmNoscriptId = "gtm-noscript";

  if (!document.getElementById(gtmScriptId)) {
    const script = document.createElement("script");
    script.id = gtmScriptId;
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);
  }

  if (!document.getElementById(gtmNoscriptId)) {
    const noscript = document.createElement("noscript");
    noscript.id = gtmNoscriptId;
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noscript);
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = (): void => resolve();
    script.onerror = (): void => reject();
    document.head.appendChild(script);
  });
}

function sendEvent(event: object): void {
  if (["labs", "dev"].includes(config.env)) {
    // eslint-disable-next-line no-console
    console.log("[Google Analytics]", event);
  }
  window.dataLayer.push(event);
}

async function fetchScript(): Promise<void> {
  const scriptSrc = `https://www.googletagmanager.com/gtm.js?id=${config.googleTagManagerId}`;
  return loadScript(scriptSrc).catch(() => {});
}

function eventStart(): void {
  sendEvent({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
}

// The signup event should be sent only:
// - If the license was created after the cutoff date
// - If the user is an admin (and thus can set the license flag to prevent multiple events from being sent)
// - If the signup event hasn't been sent yet (per license flag)
export const shouldSendSignupEvent = (
  licenseCreatedAt: Date,
  isAdmin: boolean,
  hasSentSignupGoogleAnalyticsEvent: boolean
): boolean => {
  return isAfter(licenseCreatedAt, new Date(SIGNUP_EVENT_CUTOFF)) && isAdmin && !hasSentSignupGoogleAnalyticsEvent;
};

export function eventSignup(identity: IAnalyticsIdentity, userType: string, licenseCreatedAt: Date): void {
  // From https://docs.google.com/spreadsheets/d/1kmYUc7mtXJAHXajwz-V6HKT6jkPl3L2xDDAAdd6FboQ/edit?gid=603526609#gid=603526609 (HD GA docs)
  // Signup should only fire ONCE per license, the first time an admin logs in. The license_created_at is the date of license creation (the actual signup date)
  // as the GA event will fire later than that.
  sendEvent({
    event: "Signup",
    user_id: identity.license_id.toString(),
    user_type: userType,
    license_created_at: licenseCreatedAt.getTime().toString(),
  });
}

async function loadGtagScript(): Promise<void> {
  // Define gtag before loading so window.gtag exists even if the script is blocked.
  window.gtag = function (): void {
    // gtag.js requires the Arguments object shape — do not refactor to rest params.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  try {
    await loadScript(`https://www.googletagmanager.com/gtag/js?id=${config.GA4PropertyId}`);
  } catch {
    return;
  }

  window.gtag("js", new Date());
  // send_page_view:false — GTM sends the page_view; gtag only inits GA4 for id reads.
  window.gtag("config", config.GA4PropertyId, { send_page_view: false });
}

function readGA4Id(field: "client_id" | "session_id"): Promise<string> {
  return new Promise(resolve => {
    window.gtag("get", config.GA4PropertyId, field, (id: string) => resolve(id));
  });
}

async function updateAccountGA(loggedInAgent: IAgent): Promise<void> {
  try {
    const [clientId, sessionId] = await Promise.all([readGA4Id("client_id"), readGA4Id("session_id")]);

    if (clientId && sessionId) {
      const agentSettings = loggedInAgent.settings || {};
      const googleAnalytics = agentSettings?.googleAnalytics;
      const { clientID: prevClientID, sessionID: prevSessionID } = googleAnalytics || {};

      if (clientId === prevClientID && sessionId === prevSessionID?.toString()) {
        return;
      }

      await ApiManager.agentApi.updateAgent(loggedInAgent.id, {
        settings: {
          googleAnalytics: {
            clientID: clientId,
            sessionID: sessionId,
          },
        },
      });
    } else {
      // eslint-disable-next-line no-console
      console.error("Failed to get GA4 clientID and sessionID");
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to fetch GA data", error);
  }
}
export async function initializeGTM(loggedInAgent: IAgent): Promise<void> {
  await fetchScript();
  await loadGtagScript();
  eventStart();
  updateAccountGA(loggedInAgent);
}
